<template>
    <div class="lista-paciente">
        <rkm-list
            :fields="fields"
            :repository="repositories.paciente"
            search-by="cadastroCidadao.nome"
            item-cursor="default"
        >
            <template v-slot="{ item }">
                <rkm-row class="flex-nowrap">
                    <rkm-col class="flex-grow-0 text-center my-auto">
                        <h4>{{ item.prontuario }}</h4>
                    </rkm-col>

                    <rkm-col class="flex-grow-1">
                        <rkm-row>
                            <rkm-col>
                                <h4>
                                    {{ item.nome }}

                                    <template v-if="item.nomeSocial">
                                        <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                            <strong>({{ item.nomeSocial }})</strong>
                                        </template>
                                    </template>

                                    <template v-if="identificacaoPaciente">
                                        <span
                                            v-if="item.obitoEm"
                                            class="ml-1"
                                            title="Paciente em óbito"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="cross" />
                                        </span>

                                        <span
                                            v-if="item.bloqueado"
                                            class="ml-1"
                                            title="Cadastro bloqueado"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="ban" />
                                        </span>

                                        <span
                                            v-if="isProvisorioOuTemporario(item)"
                                            class="ml-1"
                                            title="Paciente provisório/temporário"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="hourglass-half" />
                                        </span>

                                        <span
                                            v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                                            class="ml-1"
                                            title="Paciente não munícipe"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="map-pin" />
                                        </span>

                                        <span
                                            v-if="!item.cpf && !item.cns"
                                            class="ml-1"
                                            title="Paciente sem CPF e CNS"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="exclamation-triangle" />
                                        </span>

                                        <span
                                            v-if="item.pacienteResponsavelFamiliar"
                                            class="ml-1"
                                            title="Responsável familiar"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="crown" />
                                        </span>

                                        <span
                                            v-if="isIdoso(item.dataNascimento)"
                                            class="ml-1"
                                            title="Idoso"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="blind" />
                                        </span>

                                        <span
                                            v-if="hasTea(item)"
                                            class="ml-1"
                                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="infinity" />
                                        </span>

                                        <span
                                            v-if="hasPcd(item)"
                                            class="ml-1"
                                            title="Pessoa com Deficiência - PCD"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="wheelchair" />
                                        </span>
                                    </template>
                                </h4>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row class="justify-content-between">
                            <rkm-col cols="auto">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>Nome da mãe</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            {{ item.nomeMae || '-' }}
                                        </span>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>

                            <rkm-col cols="auto">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>Município de residência</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <template v-if="item.municipioNome">
                                                {{ item.municipioNome }}
                                                -
                                                {{ item.uf }}
                                            </template>

                                            <template v-else>
                                                -
                                            </template>
                                        </span>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row class="justify-content-between">
                            <rkm-col cols="auto">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>Data de nascimento</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <template v-if="item.dataNascimento">
                                                {{ item.dataNascimento | formatDate }}
                                            </template>

                                            <template v-else>
                                                -
                                            </template>
                                        </span>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>

                            <rkm-col cols="auto">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>CNS</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <template v-if="item.cns">
                                                {{ item.cns | formatCns }}
                                            </template>

                                            <template v-else>
                                                -
                                            </template>
                                        </span>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>

                            <rkm-col cols="auto">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>CPF</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <template v-if="item.cpf">
                                                {{ item.cpf | formatCpf }}
                                            </template>

                                            <template v-else>
                                                -
                                            </template>
                                        </span>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>

                            <rkm-col cols="auto">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>RG</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            {{ item.rg || '-' }}
                                        </span>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>

                            <rkm-col cols="auto" v-if="item.obitoEm">
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>Óbito</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <template>
                                    {{ item.obitoEm | formatDate }}
                                </template>
                            </rkm-col>

                            <rkm-col class="d-flex flex-grow-0">
                                <slot
                                    name="actions"
                                    v-bind:item="item"
                                />
                            </rkm-col>
                        </rkm-row>
                    </rkm-col>
                </rkm-row>
            </template>
        </rkm-list>

        <rkm-row
            v-if="identificacaoPaciente"
            class="mt-3"
        >
            <rkm-col>
                <p class="m-0">
                    <span class="legend-color purple" /> Paciente em óbito<br>
                    <span class="legend-color danger" /> Cadastro bloqueado<br>
                    <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                    <span class="legend-color brown" /> Paciente não munícipe<br>
                    <span class="legend-color warning" /> Paciente sem CPF e CNS
                </p>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
// Mixins.
import { data, repository, error } from '@/mixins'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Filters.
import formats from '@/filters/formats'

// Libs.
import moment from 'moment'

export default {
    created() {

        const paciente = this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente')

        this.repositories = {
            paciente: !this.identificacaoPaciente ? paciente : {
                ...paciente,
                datatable: params => paciente.datatable(params)
                    .then(response => {

                        response.data.data.forEach(item => item._listItemColor = this.getListItemColor(item))

                        return response
                    }),
            },
        }
    },
    data: () => ({
        fields: [
            {
                key: 'cadastroPaciente.prontuario',
                label: 'PRONTUÁRIO',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCidadao.nome',
                label: 'NOME',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCidadao.nome_social',
                label: 'NOME SOCIAL',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCidadao.nome_mae',
                label: 'NOME DA MÃE',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCidadao.data_nascimento',
                label: 'DATA DE NASCIMENTO',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCidadao.cns',
                label: 'CNS',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCidadao.cpf',
                label: 'CPF',
                sortable: true,
                searchable: true
            },
            {
                key: 'cadastroCarteiraIdentidade.numero',
                label: 'RG',
                sortable: true,
                searchable: true
            },
        ],
        enums: {
            preferenciaNome,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia,
        },
    }),
    filters: {
        ...formats
    },
    methods: {
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        getListItemColor(item) {
            if (item.obitoEm) {
                return 'purple'
            }
            if (item.bloqueado) {
                return 'danger'
            }
            if (this.isProvisorioOuTemporario(item)) {
                return 'orange'
            }
            if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'brown'
            }
            if (!item.cpf && !item.cns) {
                return 'warning'
            }
        },
    },
    mixins: [ data, repository, error ],
    props: {
        identificacaoPaciente: { type: Boolean, default: false },
    },
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.lista-paciente >>> .list-group-item-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.lista-paciente >>> .list-group-item-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.lista-paciente >>> .list-group-item-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>